import Layer from '@arcgis/core/layers/Layer';
import { isAuthoritative } from '../authoritative';
import { GISService } from '../gisServicesTypes';
import { ensureLoadedLayerArray, LayerLoader } from './LayerLoader';
import { applyCameraRenderer } from './cameraRenderer';

/**
 * Load a layer that's not otherwise special
 */
export const defaultLayerLoader: LayerLoader = {
  shouldUseLoader() {
    return true;
  },
  async load(service) {
    const properties: __esri.LayerFromArcGISServerUrlParams['properties'] = {
      id: service.gisLayerID,
      visible: service.auto_checked || false,
      // Only load OBJECTID for feature identification - reduces memory usage significantly
      // ClickToIdentify queries attributes separately when needed
      outFields: ['OBJECTID'],
      // Request 2x more features per tile - balances fewer HTTP requests vs memory usage
      maxRecordCountFactor: 2,
      // Disable popups to reduce feature metadata queries
      popupEnabled: false,
      // Disable auto-refresh - useGISLayersViewDebounce manages refresh timing
      // to only fetch when view is stationary
      refreshInterval: 0,
    };

    // Supplemental overlays are dimmed by default so they read as annotation
    // over the basemap. Authoritative layers are the PSAP's system of record
    // and render at full strength unless an admin says otherwise.
    const adminOpacity = resolveAdminOpacity(service);
    if (adminOpacity !== undefined) {
      properties.opacity = adminOpacity;
    } else if (!isAuthoritative(service)) {
      properties.opacity = 0.4;
    }

    return loadWithProperties(service, properties);
  },
};

/**
 * Convert an admin-configured opacity percentage (0-100) into the 0-1 range
 * ArcGIS expects. Returns undefined when no override exists.
 */
export function resolveOpacityPercent(
  value: number | undefined,
): number | undefined {
  if (typeof value !== 'number') {
    return undefined;
  }
  const clamped = Math.max(0, Math.min(100, value));
  return clamped / 100;
}

/**
 * Convert an admin-configured opacity (0-100, from PSAP `map_layer_settings`)
 * into the 0-1 range ArcGIS expects. Returns undefined when no admin override
 * exists, so loaders can fall back to their built-in defaults.
 */
export function resolveAdminOpacity(service: GISService): number | undefined {
  return resolveOpacityPercent(service.opacity);
}

// This pattern gets reused with different properties for several layer types:
export async function loadWithProperties(
  service: GISService,
  properties: __esri.LayerFromArcGISServerUrlParams['properties'],
) {
  const apiKey = service.token;
  const url = service.gis_service_url;
  // Keep the service URL clean. Putting `?token=` on the FeatureServer URL
  // breaks ArcGIS path joins for sublayers (`/0`, `/layers`). Pass the token
  // via apiKey + customParameters so derived requests stay `…/0?token=…`.
  const layer = await Layer.fromArcGISServerUrl({
    url,
    properties: {
      apiKey, // layer's own queries
      ...properties,
      ...(apiKey
        ? {
            customParameters: {
              ...properties?.customParameters,
              token: apiKey,
            },
          }
        : {}),
    },
  });
  await layer.load();
  applyCameraRenderer(layer);

  return ensureLoadedLayerArray(layer);
}
